import React, { useState, useRef, useEffect, useMemo } from 'react';
import { dictionary } from '../../../../dictionary';
import ProductivityRcp from '../../produtivity/resources/ProductivityRcp';
import PersoReportTableBody from './PersoReportTableBody';
import PersoReportTableFooter from './PersoReportTableFooter';
import { getWorkingDaysInMonth } from '../../utility/WorkingDaysUtils';

const PersoReport = (props) => {
  const tableprops = props.tableprops;
  const posts = tableprops.posts[0]?.posts || [];
  const team = tableprops?.team || 'Team name missing!';
  const users = tableprops.users;
  const monthName = tableprops.date.toLocaleDateString('en-US', { month: 'long' });
  const emplRate = 8;
  const emplRate2 = 7;
  const intRate = 8;
  const [workingDays, setWorkingDays] = useState(getWorkingDaysInMonth(tableprops.date));
  const [employees, setEmployyes] = useState(0);
  const [employees2, setEmployyes2] = useState(0);
  const [intEmployees, setIntEmployyes] = useState(1);
  const tableRef = useRef(null);
  const properties = {
    workingDays: workingDays,
    employees: employees,
    employees2: employees2,
    intEmployees: intEmployees,
    emplRate: emplRate,
    emplRate2: emplRate2,
    intRate: intRate,
    setWorkingDays: setWorkingDays,
    setEmployyes: setEmployyes,
    setEmployyes2: setEmployyes2,
    setIntEmployyes: setIntEmployyes,
  };

  const styles = {
    reportTable: {
      borderCollapse: 'collapse',
      width: '100%',
    },
    teamCell: {
      transform: 'rotate(360deg)',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
      writingMode: 'vertical-rl',
      textOrientation: 'upright',
      fontWeight: 'bold',
    },
    tableCell: {
      border: '1px solid #ccc',
      padding: '8px',
      textAlign: 'center',
    },
    sumCell: {
      border: '1px solid #ccc',
      backgroundColor: '#f2f2f2',
      padding: '8px',
      textAlign: 'center',
    },
    headerCell: {
      backgroundColor: '#f2f2f2',
      fontWeight: 'bold',
    },
    default: {
      background: '#f8f9fa',
    }
  };

  const teamColors = {
    'Localization Team': '#A9D08E',
    'Content Team': '#FFD966',
    'Development Team': '#9BC2E6',
    'QC Team': '#F4B084',
    'Free Team': '#CC99FF',
    'All Teams': '#f2f2f2',
  };

  const processedData = useMemo(() => {
    const aggregatedData = {};

    if (!posts) {
      return [];
    }

    const specialUsersSet = new Set(
      users.filter((userObj) => userObj.person === 'special').map((userObj) => userObj.name)
    );

    posts.forEach((post) => {
      if (post.esp !== '') {
        return;
      }
      const user = post.user;
      const hours = post.hours || 0;

      const workingHoursPerDay = specialUsersSet.has(user) ? 7 : 8;

      if (!aggregatedData[user]) {
        aggregatedData[user] = {
          user: user,
          team: team,
          workingHoursPerDay: workingHoursPerDay,
          worked_hours: 0,
          l4_hours: 0,
          childcare_hours: 0,
          vacation_hours: 0,
          special_hours: 0,
          special_hours_from_work_exemption: 0,
          total_sum: 0,
        };
      }

      switch (post.module) {
        case 'Zwolnienie lekarskie':
          aggregatedData[user].l4_hours += hours;
          break;
        case 'Opieka na dziecko':
          aggregatedData[user].childcare_hours += hours;
          break;
        case 'Urlop':
          aggregatedData[user].vacation_hours += hours;
          break;
        case 'Zwolnienie z obowiązku świadczenia pracy':
          aggregatedData[user].special_hours_from_work_exemption += hours;
          break;
        default:
          aggregatedData[user].worked_hours += hours;
          break;
      }
      aggregatedData[user].total_sum += hours;
    });

    return Object.values(aggregatedData).map((data) => {
      const standardMonthlyHours = workingDays * data.workingHoursPerDay;
      const l4_days = data.l4_hours / data.workingHoursPerDay;
      const vacation_days = data.vacation_hours / data.workingHoursPerDay;
      const special_days = data.special_hours_from_work_exemption / data.workingHoursPerDay;
      const childcare_days_calc = data.childcare_hours / data.workingHoursPerDay;
      const overtime = data.total_sum - standardMonthlyHours;

      return {
        ...data,
        standardMonthlyHours,
        worked_days: Math.round(Math.min(data.worked_hours / data.workingHoursPerDay, workingDays)),
        overtime: overtime.toFixed(2),
        overtime_color: overtime >= 0 ? 'green' : 'red',
        l4_days: Math.round(l4_days),
        special_hours: data.special_hours_from_work_exemption.toFixed(2),
        special_days: Math.round(special_days),
        childcare_days: childcare_days_calc.toFixed(2),
        vacation_days: Math.round(vacation_days),
      };
    });
  }, [posts, team, workingDays, users]);

  const groupedData = useMemo(() => {
    const groups = {};
    if (processedData) {
      const sortedData = [...processedData].sort((a, b) => a.team.localeCompare(b.team));
      sortedData.forEach((item) => {
        if (!groups[item.team]) {
          groups[item.team] = [];
        }
        groups[item.team].push(item);
      });
    }
    return groups;
  }, [processedData]);

  const totalSums = useMemo(() => {
    const initialSums = {
      standardMonthlyHours: 0,
      worked_hours: 0,
      worked_days: 0,
      overtime: 0,
      l4_hours: 0,
      l4_days: 0,
      special_hours: 0,
      special_days: 0,
      childcare_hours: 0,
      childcare_days: 0,
      vacation_hours: 0,
      vacation_days: 0,
      total_sum: 0,
    };

    return processedData.reduce((acc, current) => {
      acc.standardMonthlyHours += current.standardMonthlyHours;
      acc.worked_hours += current.worked_hours;
      acc.worked_days += current.worked_days;
      acc.overtime += parseFloat(current.overtime);
      acc.l4_hours += current.l4_hours;
      acc.l4_days += current.l4_days;
      acc.special_hours += parseFloat(current.special_hours);
      acc.special_days += current.special_days;
      acc.childcare_hours += current.childcare_hours;
      acc.childcare_days += parseFloat(current.childcare_days);
      acc.vacation_hours += current.vacation_hours;
      acc.vacation_days += current.vacation_days;
      acc.total_sum += current.total_sum;
      return acc;
    }, initialSums);
  }, [processedData]);

  useEffect(() => {
    let specialUsersCount = 0;
    let normalUsersCount = 0;
    for (const user of users) {
      if (user.person === 'special') {
        specialUsersCount++;
      } else {
        normalUsersCount++;
      }
    }
    setEmployyes2(specialUsersCount);
    setEmployyes(normalUsersCount);
  }, [users]);

  useEffect(() => {
    tableprops.setFixRef(tableRef.current);
  }, [tableprops.setFixRef]);

  return (
    <div className="perso-container">
      <h2>{tableprops.team + dictionary.perso_teams_date + monthName + ' ' + tableprops.date.getFullYear()}</h2>
      <p>{dictionary.perso_rcp_info}</p>
      <div className="rcp-table-wrapper">
        <ProductivityRcp
          properties={properties}
          visibleColumns={['workingDays', 'employees', 'employees2', 'intEmployees']}
        />
      </div>
      <table ref={tableRef} style={styles.reportTable}>
        <thead>
          <tr>
            {[
              'l.p',
              'TEAM',
              'USER',
              'WYMIAR GODZIN',
              'ILE GODZIN W MIESIĄCU POWINNO BYĆ',
              'ILE PRACOWAŁ GODZIN W MIESIĄCU',
              'ILE DNI PRACOWAŁ',
              'NADGODZINY/ NIEDOGODZINY',
              'L4 GODZINY',
              'L4 DNI',
              'SPECJALNE GODZINY',
              'SPECJALNE DNI',
              'DZIECKO GODZINY',
              'DZIECKO DNI',
              'URLOP GODZINY',
              'URLOP DNI',
              'SUMA CAŁKOWITA',
            ].map((header) => (
              <th key={header} style={{ ...styles.tableCell, ...styles.headerCell }}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <PersoReportTableBody groupedData={groupedData} styles={styles} teamColors={teamColors} />
        <PersoReportTableFooter totalSums={totalSums} styles={styles} />
      </table>
    </div>
  );
};

export default PersoReport;
